import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  MapPin,
  Plus,
  Loader2,
  RefreshCw,
  AlertCircle,
  Search,
  Edit,
  Trash2,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  ChevronRight,
  Sun,
  Droplets,
  Car,
  Home,
  Store,
  Sofa,
  X,
  Check,
} from 'lucide-react';

// Sort types
type SortDirection = 'asc' | 'desc' | null;
interface SortConfig {
  key: string;
  direction: SortDirection;
}

// Types
interface CourtUnit {
  id: number;
  facilityId: number;
  label: string;
  sportType: string;
  sortOrder: number;
  active: boolean;
  accessType: string;
}

interface Court {
  id: number;
  name: string;
  address: string;
  pictureUrl?: string;
  latitude: number;
  longitude: number;
  accessType: string;
  sportType: string;
  indoor: boolean;
  lights: boolean;
  restrooms: boolean;
  parking: boolean;
  waterFountain: boolean;
  seating: boolean;
  proShop: boolean;
  active: boolean;
  unitCount: number;
  units?: CourtUnit[];
}

interface PageResponse {
  content: Court[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

// API helper
async function fetchApi<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `HTTP ${response.status}`);
  }

  return response.json();
}

export function CourtsPage() {
  // State
  const [courts, setCourts] = useState<Court[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);

  // Pagination
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // Expanded courts (to show units)
  const [expandedCourts, setExpandedCourts] = useState<Set<number>>(new Set());

  // Edit modal
  const [editingCourt, setEditingCourt] = useState<Court | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sorting
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  // Load courts
  const loadCourts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      let url = `/api/v1/admin/courts?page=${page}&size=20`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (activeFilter !== undefined) url += `&active=${activeFilter}`;

      const data = await fetchApi<PageResponse>(url);
      setCourts(data.content);
      setTotalPages(data.totalPages);
      setTotalItems(data.totalElements);
    } catch (err) {
      console.error('Failed to load courts:', err);
      // Use mock data for development
      setCourts(getMockCourts());
      setTotalPages(1);
      setTotalItems(5);
    } finally {
      setIsLoading(false);
    }
  }, [search, activeFilter, page]);

  // Load court details (with units)
  const loadCourtDetails = async (courtId: number) => {
    try {
      const data = await fetchApi<Court>(`/api/v1/admin/courts/${courtId}`);
      setCourts(prev => prev.map(c => c.id === courtId ? data : c));
    } catch (err) {
      console.error('Failed to load court details:', err);
    }
  };

  // Toggle expanded court
  const toggleCourt = (courtId: number) => {
    setExpandedCourts(prev => {
      const next = new Set(prev);
      if (next.has(courtId)) {
        next.delete(courtId);
      } else {
        next.add(courtId);
        // Load units if not already loaded
        const court = courts.find(c => c.id === courtId);
        if (court && !court.units) {
          loadCourtDetails(courtId);
        }
      }
      return next;
    });
  };

  // Save court
  const saveCourt = async () => {
    if (!editingCourt) return;

    setIsSubmitting(true);
    try {
      if (editingCourt.id) {
        await fetchApi(`/api/v1/admin/courts/${editingCourt.id}`, {
          method: 'PUT',
          body: JSON.stringify(editingCourt),
        });
      } else {
        await fetchApi('/api/v1/admin/courts', {
          method: 'POST',
          body: JSON.stringify(editingCourt),
        });
      }
      setEditingCourt(null);
      loadCourts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save court');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete court
  const deleteCourt = async (courtId: number) => {
    if (!confirm('Are you sure you want to delete this court?')) return;

    try {
      await fetchApi(`/api/v1/admin/courts/${courtId}`, {
        method: 'DELETE',
      });
      loadCourts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete court');
    }
  };

  // Toggle court active status
  const toggleActive = async (court: Court) => {
    try {
      await fetchApi(`/api/v1/admin/courts/${court.id}`, {
        method: 'PUT',
        body: JSON.stringify({ ...court, active: !court.active }),
      });
      loadCourts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update court');
    }
  };

  // Load on mount and filter change
  useEffect(() => {
    loadCourts();
  }, [loadCourts]);

  // Mock data for development
  function getMockCourts(): Court[] {
    return [
      {
        id: 1,
        name: 'Central Park Tennis Center',
        address: '1234 Park Ave, New York, NY 10001',
        latitude: 40.7829,
        longitude: -73.9654,
        accessType: 'PUBLIC',
        sportType: 'TENNIS',
        indoor: false,
        lights: true,
        restrooms: true,
        parking: true,
        waterFountain: true,
        seating: true,
        proShop: false,
        active: true,
        unitCount: 8,
      },
      {
        id: 2,
        name: 'Brooklyn Sports Complex',
        address: '5678 Atlantic Ave, Brooklyn, NY 11217',
        latitude: 40.6782,
        longitude: -73.9442,
        accessType: 'PUBLIC',
        sportType: 'BOTH',
        indoor: true,
        lights: true,
        restrooms: true,
        parking: true,
        waterFountain: true,
        seating: true,
        proShop: true,
        active: true,
        unitCount: 12,
      },
      {
        id: 3,
        name: 'Queens Tennis Club',
        address: '9012 Queens Blvd, Queens, NY 11375',
        latitude: 40.7282,
        longitude: -73.8317,
        accessType: 'PRIVATE',
        sportType: 'TENNIS',
        indoor: false,
        lights: true,
        restrooms: true,
        parking: true,
        waterFountain: false,
        seating: true,
        proShop: true,
        active: true,
        unitCount: 6,
      },
      {
        id: 4,
        name: 'Riverside Pickleball Courts',
        address: '3456 Riverside Dr, Manhattan, NY 10025',
        latitude: 40.8012,
        longitude: -73.9665,
        accessType: 'PUBLIC',
        sportType: 'PICKLEBALL',
        indoor: false,
        lights: false,
        restrooms: false,
        parking: false,
        waterFountain: true,
        seating: false,
        proShop: false,
        active: true,
        unitCount: 4,
      },
      {
        id: 5,
        name: 'Old Tennis Courts (Closed)',
        address: '7890 Old St, Bronx, NY 10451',
        latitude: 40.8176,
        longitude: -73.9230,
        accessType: 'PUBLIC',
        sportType: 'TENNIS',
        indoor: false,
        lights: false,
        restrooms: false,
        parking: false,
        waterFountain: false,
        seating: false,
        proShop: false,
        active: false,
        unitCount: 2,
      },
    ];
  }

  // Handle sort
  const handleSort = (key: string) => {
    setSortConfig((current) => {
      if (current?.key !== key) {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return null;
    });
  };

  // Sort data
  const sortedCourts = useMemo(() => {
    if (!sortConfig || !sortConfig.direction) {
      return courts;
    }

    return [...courts].sort((a, b) => {
      const aValue = a[sortConfig.key as keyof Court];
      const bValue = b[sortConfig.key as keyof Court];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;

      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue, undefined, { sensitivity: 'base' });
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        comparison = aValue === bValue ? 0 : aValue ? -1 : 1;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [courts, sortConfig]);

  // Sortable header component
  const SortableHeader = ({ label, sortKey, className = '' }: { label: string; sortKey: string; className?: string }) => {
    const isActive = sortConfig?.key === sortKey;
    const direction = isActive ? sortConfig.direction : null;

    return (
      <th
        className={`px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer select-none hover:text-slate-200 hover:bg-slate-800/50 transition-colors ${className}`}
        onClick={() => handleSort(sortKey)}
      >
        <div className="flex items-center gap-1.5">
          <span>{label}</span>
          <span className="flex-shrink-0">
            {direction === 'asc' ? (
              <ChevronUp className="h-4 w-4 text-cyan-400" />
            ) : direction === 'desc' ? (
              <ChevronDown className="h-4 w-4 text-cyan-400" />
            ) : (
              <ChevronsUpDown className="h-3.5 w-3.5 text-slate-600" />
            )}
          </span>
        </div>
      </th>
    );
  };

  // Amenity badge component
  const AmenityBadge = ({ icon: Icon, label, active }: { icon: typeof Sun; label: string; active: boolean }) => (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
        active
          ? 'bg-cyan-500/20 text-cyan-400'
          : 'bg-slate-700/50 text-slate-500'
      }`}
      title={label}
    >
      <Icon className="h-3 w-3" />
    </span>
  );

  return (
    <div className="flex flex-col min-h-0 h-full">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <MapPin className="h-7 w-7 text-cyan-400" />
            Courts
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Manage tennis and pickleball courts
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadCourts}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 rounded-lg transition-colors"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </button>
          <button
            onClick={() => setEditingCourt({
              id: 0,
              name: '',
              address: '',
              latitude: 40.7128,
              longitude: -74.0060,
              accessType: 'PUBLIC',
              sportType: 'BOTH',
              indoor: false,
              lights: false,
              restrooms: false,
              parking: false,
              waterFountain: false,
              seating: false,
              proShop: false,
              active: true,
              unitCount: 0,
            })}
            className="inline-flex items-center gap-2 px-3 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Court
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search courts..."
            className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
          />
        </div>

        {/* Active filter */}
        <div className="flex items-center gap-1 p-1 bg-slate-800 rounded-lg">
          {[
            { key: undefined, label: 'All' },
            { key: true, label: 'Active' },
            { key: false, label: 'Inactive' },
          ].map(({ key, label }) => (
            <button
              key={String(key)}
              onClick={() => { setActiveFilter(key); setPage(0); }}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                activeFilter === key
                  ? 'bg-cyan-600 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 p-4 mb-4 bg-red-900/20 border border-red-800/50 rounded-lg text-red-400">
          <AlertCircle className="h-5 w-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto rounded-lg border border-slate-800">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
          </div>
        ) : courts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-500">
            <MapPin className="h-12 w-12 mb-3 opacity-50" />
            <p>No courts found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-800/50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider w-8"></th>
                <SortableHeader label="Name" sortKey="name" />
                <SortableHeader label="Address" sortKey="address" />
                <SortableHeader label="Type" sortKey="sportType" />
                <SortableHeader label="Courts" sortKey="unitCount" />
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Amenities</th>
                <SortableHeader label="Status" sortKey="active" />
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {sortedCourts.map((court) => {
                const isExpanded = expandedCourts.has(court.id);
                return (
                  <>
                    <tr key={court.id} className="hover:bg-slate-800/30">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleCourt(court.id)}
                          className="p-1 rounded hover:bg-slate-700"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-slate-400" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-white">{court.name}</div>
                        <div className="text-xs text-slate-500">
                          {court.latitude.toFixed(4)}, {court.longitude.toFixed(4)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-300">{court.address || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          court.sportType === 'TENNIS' ? 'bg-green-500/20 text-green-400' :
                          court.sportType === 'PICKLEBALL' ? 'bg-purple-500/20 text-purple-400' :
                          'bg-cyan-500/20 text-cyan-400'
                        }`}>
                          {court.sportType}
                        </span>
                        <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                          court.accessType === 'PUBLIC' ? 'bg-slate-700 text-slate-300' : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {court.accessType}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-300">{court.unitCount}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <AmenityBadge icon={Sun} label="Lights" active={court.lights} />
                          <AmenityBadge icon={Droplets} label="Restrooms" active={court.restrooms} />
                          <AmenityBadge icon={Car} label="Parking" active={court.parking} />
                          <AmenityBadge icon={Home} label="Indoor" active={court.indoor} />
                          <AmenityBadge icon={Store} label="Pro Shop" active={court.proShop} />
                          <AmenityBadge icon={Sofa} label="Seating" active={court.seating} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleActive(court)}
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            court.active
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {court.active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setEditingCourt(court)}
                            className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-white"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deleteCourt(court.id)}
                            className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-red-400"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {/* Expanded units row */}
                    {isExpanded && (
                      <tr key={`${court.id}-units`}>
                        <td colSpan={8} className="px-4 py-3 bg-slate-800/20">
                          <div className="ml-8 space-y-2">
                            <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Court Units</div>
                            {court.units ? (
                              court.units.length > 0 ? (
                                <div className="grid grid-cols-4 gap-2">
                                  {court.units.map((unit) => (
                                    <div
                                      key={unit.id}
                                      className={`p-3 rounded-lg border ${
                                        unit.active
                                          ? 'bg-slate-800/50 border-slate-700'
                                          : 'bg-slate-900/50 border-slate-800 opacity-60'
                                      }`}
                                    >
                                      <div className="font-medium text-white">{unit.label}</div>
                                      <div className="text-xs text-slate-500">
                                        {unit.sportType} • {unit.accessType}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-sm text-slate-500">No court units defined</div>
                              )
                            ) : (
                              <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800">
          <div className="text-sm text-slate-500">
            Showing {courts.length} of {totalItems} courts
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 text-sm rounded-lg transition-colors"
            >
              Previous
            </button>
            <span className="text-sm text-slate-400">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 text-sm rounded-lg transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingCourt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <h2 className="text-lg font-medium text-white">
                {editingCourt.id ? 'Edit Court' : 'Add Court'}
              </h2>
              <button
                onClick={() => setEditingCourt(null)}
                className="p-1 rounded hover:bg-slate-800 text-slate-400"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Name *</label>
                <input
                  type="text"
                  value={editingCourt.name}
                  onChange={(e) => setEditingCourt({ ...editingCourt, name: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
                />
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Address</label>
                <input
                  type="text"
                  value={editingCourt.address}
                  onChange={(e) => setEditingCourt({ ...editingCourt, address: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
                />
              </div>

              {/* Location */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Latitude *</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={editingCourt.latitude}
                    onChange={(e) => setEditingCourt({ ...editingCourt, latitude: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Longitude *</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={editingCourt.longitude}
                    onChange={(e) => setEditingCourt({ ...editingCourt, longitude: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  />
                </div>
              </div>

              {/* Type and Access */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Sport Type</label>
                  <select
                    value={editingCourt.sportType}
                    onChange={(e) => setEditingCourt({ ...editingCourt, sportType: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  >
                    <option value="BOTH">Both</option>
                    <option value="TENNIS">Tennis</option>
                    <option value="PICKLEBALL">Pickleball</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Access Type</label>
                  <select
                    value={editingCourt.accessType}
                    onChange={(e) => setEditingCourt({ ...editingCourt, accessType: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  >
                    <option value="PUBLIC">Public</option>
                    <option value="PRIVATE">Private</option>
                  </select>
                </div>
              </div>

              {/* Amenities */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Amenities</label>
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { key: 'indoor', label: 'Indoor' },
                    { key: 'lights', label: 'Lights' },
                    { key: 'restrooms', label: 'Restrooms' },
                    { key: 'parking', label: 'Parking' },
                    { key: 'waterFountain', label: 'Water Fountain' },
                    { key: 'seating', label: 'Seating' },
                    { key: 'proShop', label: 'Pro Shop' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingCourt[key as keyof Court] as boolean}
                        onChange={(e) => setEditingCourt({ ...editingCourt, [key]: e.target.checked })}
                        className="rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500"
                      />
                      <span className="text-sm text-slate-300">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Active */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingCourt.active}
                  onChange={(e) => setEditingCourt({ ...editingCourt, active: e.target.checked })}
                  className="rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500"
                />
                <span className="text-sm text-slate-300">Active</span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-800">
              <button
                onClick={() => setEditingCourt(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveCourt}
                disabled={isSubmitting || !editingCourt.name || !editingCourt.latitude || !editingCourt.longitude}
                className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white rounded-lg transition-colors"
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
